// 🔥 가격 스냅샷 백업/복구 타입 정의
// [배경] 관리자 페이지에서 가격 데이터의 스냅샷 백업, 검증, 미리보기, 부분 복구, 롤백 기능 지원
// Firestore 컬렉션: price_snapshots, price_snapshots/{snapshotId}/data

import { Timestamp } from 'firebase/firestore';
import { DeviceType, DeviceBrand, UnifiedPriceDocument } from './deviceCatalog';

// ============================================================
// 📦 스냅샷 메타데이터 인터페이스
// ============================================================

/**
 * 브랜드-디바이스타입별 가격 데이터 분포
 */
export interface SnapshotBreakdown {
  'Apple-smartphone': number;
  'Apple-tablet': number;
  'Apple-wearable': number;
  'Samsung-smartphone': number;
  'Samsung-tablet': number;
}

/**
 * 스냅샷 상태
 */
export type SnapshotStatus = 'completed' | 'in_progress' | 'failed';

/**
 * 검증 상태
 */
export type ValidationStatus = 'valid' | 'warning' | 'error' | 'pending';

/**
 * 가격 스냅샷 메타데이터 (Firestore: price_snapshots 컬렉션)
 */
export interface PriceSnapshot {
  // 🔑 고유 식별자
  id: string;                           // "snapshot_2024-12-23_143022"
  
  // 📅 생성 정보
  createdAt: Timestamp | Date;          // 생성 시간
  createdBy: string;                    // 관리자 ID
  description: string;                  // "월간 정기 백업", "가격 대폭 변경 전 백업"
  
  // 📊 상태 정보
  status: SnapshotStatus;               // 스냅샷 생성 상태
  totalPrices: number;                  // 총 가격 레코드 수
  breakdown: SnapshotBreakdown;         // 브랜드/디바이스별 분포
  
  // 📁 저장 정보
  fileSize: number;                     // 추정 크기 (bytes)
  chunkCount: number;                   // 데이터 청크 수
  
  // ✅ 검증 정보
  validationStatus: ValidationStatus;
  validationMessage?: string;
  validationWarnings?: string[];
  
  // 📝 메타데이터
  version: string;                      // 스냅샷 포맷 버전 (예: "1.0")
  sourceCollection: string;             // 원본 컬렉션명 ("unified_prices")
}

// ============================================================
// 📄 스냅샷 데이터 청크 인터페이스
// ============================================================

/**
 * 스냅샷 데이터 청크 (Firestore: price_snapshots/{snapshotId}/data)
 * 대용량 데이터를 청크 단위로 분할 저장
 */
export interface SnapshotDataChunk {
  id: string;                           // "chunk_0", "chunk_1", ...
  chunkIndex: number;                   // 청크 순서 (0부터 시작)
  totalChunks: number;                  // 전체 청크 수
  data: UnifiedPriceDocument[];         // 실제 가격 데이터 배열
  recordCount: number;                  // 이 청크의 레코드 수
  createdAt: Timestamp | Date;
}

// ============================================================
// 🔍 필터 및 검색 인터페이스
// ============================================================

/**
 * 스냅샷 필터 옵션
 */
export interface SnapshotFilter {
  brand?: DeviceBrand | 'all';          // 브랜드 필터
  deviceType?: DeviceType | 'all';      // 디바이스 타입 필터
  modelIds?: string[];                  // 특정 모델만 선택
}

/**
 * 스냅샷 목록 조회 옵션
 */
export interface SnapshotListOptions {
  limit?: number;                       // 조회 개수 제한
  orderBy?: 'createdAt' | 'totalPrices';
  orderDirection?: 'asc' | 'desc';
  statusFilter?: SnapshotStatus;
}

// ============================================================
// 📊 Diff (변경사항 비교) 인터페이스
// ============================================================

/**
 * 변경 유형
 */
export type DiffType = 'added' | 'removed' | 'modified' | 'unchanged';

/**
 * 개별 가격 변경 항목
 */
export interface PriceDiffItem {
  priceId: string;                      // unified_prices 문서 ID
  modelId: string;
  modelName: string;
  brand: DeviceBrand;
  deviceType: DeviceType;
  storage: string;
  connectivity?: string;
  
  diffType: DiffType;
  
  // 가격 변경 상세 (등급별)
  currentPrices?: {
    A: number;
    B: number;
    C: number;
    D: number;
  };
  snapshotPrices?: {
    A: number;
    B: number;
    C: number;
    D: number;
  };
  
  // 변경 요약
  priceChanges?: {
    grade: 'A' | 'B' | 'C' | 'D';
    currentValue: number;
    snapshotValue: number;
    difference: number;
    percentChange: number;
  }[];
}

/**
 * Diff 결과 요약
 */
export interface DiffSummary {
  totalItems: number;
  added: number;
  removed: number;
  modified: number;
  unchanged: number;
  
  // 브랜드/디바이스별 분포
  byBrand: Record<DeviceBrand, { added: number; removed: number; modified: number }>;
  byDeviceType: Record<DeviceType, { added: number; removed: number; modified: number }>;
}

/**
 * 전체 Diff 결과
 */
export interface PriceDiffResult {
  snapshotId: string;
  snapshotDate: Date;
  filter: SnapshotFilter;
  
  summary: DiffSummary;
  items: PriceDiffItem[];
  
  // 계산 메타데이터
  calculatedAt: Date;
  processingTimeMs: number;
}

// ============================================================
// ✅ 검증 인터페이스
// ============================================================

/**
 * 검증 이슈 타입
 */
export type ValidationIssueType = 
  | 'missing_price'           // 가격 누락
  | 'invalid_price_range'     // 가격 범위 이상 (음수, 과도하게 높음)
  | 'grade_monotonicity'      // 등급별 단조감소 위반 (A < B 등)
  | 'duplicate_entry'         // 중복 항목
  | 'invalid_model_reference' // 존재하지 않는 모델 참조
  | 'data_integrity';         // 기타 데이터 무결성 문제

/**
 * 검증 이슈 심각도
 */
export type ValidationSeverity = 'error' | 'warning' | 'info';

/**
 * 개별 검증 이슈
 */
export interface ValidationIssue {
  type: ValidationIssueType;
  severity: ValidationSeverity;
  message: string;
  priceId?: string;
  modelId?: string;
  field?: string;
  currentValue?: any;
  expectedValue?: any;
}

/**
 * 검증 결과
 */
export interface ValidationResult {
  isValid: boolean;                     // 에러 없음 여부
  status: ValidationStatus;
  
  // 이슈 카운트
  errorCount: number;
  warningCount: number;
  infoCount: number;
  
  // 상세 이슈 목록
  issues: ValidationIssue[];
  
  // 검증 메타데이터
  validatedAt: Date;
  totalRecordsChecked: number;
}

// ============================================================
// 🔄 복구 인터페이스
// ============================================================

/**
 * 복구 모드
 */
export type RestoreMode = 
  | 'replace'      // 기존 데이터 삭제 후 교체
  | 'merge'        // 기존 데이터와 병합 (스냅샷 데이터 우선)
  | 'selective';   // 선택한 항목만 복구

/**
 * 복구 옵션
 */
export interface RestoreOptions {
  mode: RestoreMode;
  filter: SnapshotFilter;               // 복구 범위
  createBackupBefore: boolean;          // 복구 전 현재 데이터 백업 생성
  skipValidation?: boolean;             // 검증 건너뛰기 (주의 필요)
  dryRun?: boolean;                     // 시뮬레이션 모드 (실제 저장 안함)
}

/**
 * 복구 진행 상태
 */
export interface RestoreProgress {
  phase: 'preparing' | 'validating' | 'backing_up' | 'restoring' | 'verifying' | 'completed' | 'failed';
  currentStep: number;
  totalSteps: number;
  processedRecords: number;
  totalRecords: number;
  message: string;
  errors?: string[];
}

/**
 * 복구 결과
 */
export interface RestoreResult {
  success: boolean;
  snapshotId: string;
  restoredAt: Date;
  
  // 복구 통계
  statistics: {
    totalRestored: number;
    added: number;
    updated: number;
    deleted: number;
    skipped: number;
    failed: number;
  };
  
  // 백업 정보 (복구 전 생성된 경우)
  preRestoreBackupId?: string;
  
  // 에러 정보
  errors?: {
    priceId: string;
    error: string;
  }[];
  
  // 처리 시간
  durationMs: number;
}

// ============================================================
// 📋 복구 마법사 스텝 인터페이스
// ============================================================

/**
 * 마법사 스텝
 */
export type WizardStep = 
  | 'select_snapshot'   // Step 1: 스냅샷 선택
  | 'select_scope'      // Step 2: 복구 범위 선택
  | 'diff_preview'      // Step 3: 변경사항 미리보기
  | 'validation'        // Step 4: 데이터 검증
  | 'confirm_restore';  // Step 5: 최종 확인 및 실행

/**
 * 마법사 상태
 */
export interface WizardState {
  currentStep: WizardStep;
  selectedSnapshot: PriceSnapshot | null;
  filter: SnapshotFilter;
  diffResult: PriceDiffResult | null;
  validationResult: ValidationResult | null;
  restoreOptions: RestoreOptions;
  isProcessing: boolean;
  error: string | null;
}

// ============================================================
// 🛠️ 유틸리티 타입
// ============================================================

/**
 * 스냅샷 ID 생성 함수 반환 타입
 */
export type SnapshotIdGenerator = () => string;

/**
 * 스냅샷 생성 요청
 */
export interface CreateSnapshotRequest {
  description: string;
  adminId: string;
  filter?: SnapshotFilter;              // 특정 범위만 백업 (선택사항)
}

/**
 * 스냅샷 생성 결과
 */
export interface CreateSnapshotResult {
  success: boolean;
  snapshotId: string;
  snapshot?: PriceSnapshot;
  error?: string;
  durationMs: number;
}

// ============================================================
// 📊 상수 정의
// ============================================================

/**
 * 스냅샷 관련 상수
 */
export const SNAPSHOT_CONSTANTS = {
  // Firestore 컬렉션명
  COLLECTION: 'price_snapshots',
  DATA_SUBCOLLECTION: 'data',
  
  // 청크 설정
  CHUNK_SIZE: 100,                      // 청크당 최대 레코드 수
  MAX_CHUNKS: 50,                       // 최대 청크 수 (5000개 레코드)
  
  // 검증 규칙
  MIN_PRICE: 0,
  MAX_PRICE: 10_000_000,                // 1천만원
  
  // 버전
  CURRENT_VERSION: '1.0',
  
  // 보관 기간
  DEFAULT_RETENTION_DAYS: 90,           // 기본 90일 보관
} as const;

/**
 * 마법사 스텝 정보
 */
export const WIZARD_STEPS: { step: WizardStep; label: string; description: string }[] = [
  { step: 'select_snapshot', label: '스냅샷 선택', description: '복구할 백업 스냅샷을 선택합니다' },
  { step: 'select_scope', label: '복구 범위', description: '브랜드/디바이스 타입별 복구 범위를 지정합니다' },
  { step: 'diff_preview', label: '변경 미리보기', description: '현재 데이터와 백업 데이터의 차이를 확인합니다' },
  { step: 'validation', label: '데이터 검증', description: '복구할 데이터의 유효성을 검증합니다' },
  { step: 'confirm_restore', label: '복구 실행', description: '최종 확인 후 데이터를 복구합니다' },
];

